import {
  Cog,
  Plus,
  Search,
  Edit2,
  Trash2,
  X,
  AlertCircle,
  CheckCircle,
  Loader2,
  Save,
  Filter,
  ChevronDown,
  Hash,
  FileText,
  Code,
  Wrench,
  AlertTriangle,
} from 'lucide-react';
import { useState, useEffect, useMemo } from 'react';

import { supabase } from '../../lib/supabase';
import { useAuth } from '../../providers/AuthProvider';

// ── Types ───────────────────────────────────────────────────────────────────

interface Equipment {
  id: string;
  tenant_id: string;
  location_id: string | null;
  name: string;
  code: string | null;
  type: string;
  description: string | null;
  manufacturer: string | null;
  model: string | null;
  serial_number: string | null;
  year_of_manufacture: number | null;
  purchase_date: string | null;
  warranty_expires: string | null;
  supplier_id: string | null;
  status: 'operational' | 'warning' | 'critical' | 'maintenance' | 'decommissioned';
  is_active: boolean;
  module_data: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

interface EquipmentFormData {
  name: string;
  code: string;
  type: string;
  status: string;
  description: string;
  manufacturer: string;
  model: string;
  serial_number: string;
  year_of_manufacture: string;
  module_data: string;
}

// ── Constants ───────────────────────────────────────────────────────────────

const EQUIPMENT_TYPES = [
  { value: 'refrigerator', label: 'Chłodnia', icon: 'Thermometer' },
  { value: 'freezer', label: 'Zamrażarka', icon: 'Snowflake' },
  { value: 'oven', label: 'Piec', icon: 'Flame' },
  { value: 'mixer', label: 'Mieszalnik', icon: 'Cog' },
  { value: 'packaging_machine', label: 'Pakowarka', icon: 'Package' },
  { value: 'conveyor', label: 'Transporter', icon: 'ArrowRightLeft' },
  { value: 'scale', label: 'Waga', icon: 'Scale' },
  { value: 'printer', label: 'Drukarka', icon: 'Printer' },
  { value: 'sensor', label: 'Czujnik', icon: 'Radio' },
  { value: 'custom', label: 'Inny', icon: 'Wrench' },
];

const STATUS_OPTIONS = [
  { value: 'operational', label: 'Sprawny', color: 'text-green-400', bg: 'bg-green-500/20' },
  { value: 'warning', label: 'Ostrzeżenie', color: 'text-yellow-400', bg: 'bg-yellow-500/20' },
  { value: 'critical', label: 'Krytyczny', color: 'text-red-400', bg: 'bg-red-500/20' },
  { value: 'maintenance', label: 'Serwis', color: 'text-blue-400', bg: 'bg-blue-500/20' },
  { value: 'decommissioned', label: 'Wycofany', color: 'text-gray-400', bg: 'bg-gray-500/20' },
];

const initialFormData: EquipmentFormData = {
  name: '',
  code: '',
  type: 'refrigerator',
  status: 'operational',
  description: '',
  manufacturer: '',
  model: '',
  serial_number: '',
  year_of_manufacture: '',
  module_data: '{}',
};

// ── Component ───────────────────────────────────────────────────────────────

export function AdminEquipmentPage() {
  const { currentOrganization, hasRole } = useAuth();

  // Data
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Search & filter
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState<Equipment | null>(null);
  const [formData, setFormData] = useState<EquipmentFormData>(initialFormData);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Delete
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const canManage = hasRole(['owner', 'admin', 'manager']);

  // ── Fetch ─────────────────────────────────────────────────────────────

  useEffect(() => {
    if (currentOrganization) fetchEquipment();
  }, [currentOrganization]);

  const fetchEquipment = async () => {
    if (!currentOrganization) return;
    setIsLoading(true);
    try {
      const { data, error: err } = await supabase
        .from('equipment')
        .select('*')
        .eq('tenant_id', currentOrganization.organization_id)
        .order('name');
      if (err) throw err;
      setEquipment(data || []);
    } catch (err) {
      console.error('Error fetching equipment:', err);
      setError('Nie udało się załadować sprzętu');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Derived ───────────────────────────────────────────────────────────

  const filteredEquipment = useMemo(() => {
    return equipment.filter((e) => {
      const q = searchQuery.toLowerCase();
      const matchesSearch =
        !q ||
        e.name.toLowerCase().includes(q) ||
        e.code?.toLowerCase().includes(q) ||
        e.type.toLowerCase().includes(q) ||
        e.manufacturer?.toLowerCase().includes(q) ||
        e.model?.toLowerCase().includes(q) ||
        e.serial_number?.toLowerCase().includes(q) ||
        e.description?.toLowerCase().includes(q);
      const matchesType = filterType === 'all' || e.type === filterType;
      const matchesStatus = filterStatus === 'all' || e.status === filterStatus;
      return matchesSearch && matchesType && matchesStatus;
    });
  }, [equipment, searchQuery, filterType, filterStatus]);

  const getTypeLabel = (type: string) =>
    EQUIPMENT_TYPES.find((t) => t.value === type)?.label || type;

  const getStatusInfo = (status: string) =>
    STATUS_OPTIONS.find((s) => s.value === status) || STATUS_OPTIONS[0];

  // ── Handlers ──────────────────────────────────────────────────────────

  const openAddModal = () => {
    setEditingEquipment(null);
    setFormData(initialFormData);
    setFormError(null);
    setShowModal(true);
  };

  const openEditModal = (item: Equipment) => {
    setEditingEquipment(item);
    setFormData({
      name: item.name,
      code: item.code || '',
      type: item.type,
      status: item.status,
      description: item.description || '',
      manufacturer: item.manufacturer || '',
      model: item.model || '',
      serial_number: item.serial_number || '',
      year_of_manufacture: item.year_of_manufacture ? String(item.year_of_manufacture) : '',
      module_data: item.module_data ? JSON.stringify(item.module_data, null, 2) : '{}',
    });
    setFormError(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingEquipment(null);
    setFormData(initialFormData);
    setFormError(null);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentOrganization) return;

    if (!formData.name.trim()) {
      setFormError('Nazwa sprzętu jest wymagana');
      return;
    }

    if (!formData.type) {
      setFormError('Typ sprzętu jest wymagany');
      return;
    }

    let parsedModuleData: Record<string, unknown> | null = null;
    if (formData.module_data.trim() && formData.module_data.trim() !== '{}') {
      try {
        parsedModuleData = JSON.parse(formData.module_data);
      } catch {
        setFormError('Dane modułu (JSON) są niepoprawne');
        return;
      }
    }

    setIsSaving(true);
    setFormError(null);

    try {
      const payload = {
        tenant_id: currentOrganization.organization_id,
        name: formData.name.trim(),
        code: formData.code.trim() || null,
        type: formData.type,
        status: formData.status,
        description: formData.description.trim() || null,
        manufacturer: formData.manufacturer.trim() || null,
        model: formData.model.trim() || null,
        serial_number: formData.serial_number.trim() || null,
        year_of_manufacture: formData.year_of_manufacture
          ? parseInt(formData.year_of_manufacture, 10)
          : null,
        module_data: parsedModuleData,
      };

      if (editingEquipment) {
        const { error: err } = await supabase
          .from('equipment')
          .update(payload)
          .eq('id', editingEquipment.id);
        if (err) throw err;
        setSuccess('Sprzęt został zaktualizowany');
      } else {
        const { error: err } = await supabase
          .from('equipment')
          .insert([{ ...payload, is_active: true }]);
        if (err) {
          if (err.code === '23505') {
            setFormError('Sprzęt o tym kodzie już istnieje');
            setIsSaving(false);
            return;
          }
          throw err;
        }
        setSuccess('Sprzęt został dodany');
      }

      await fetchEquipment();
      closeModal();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving equipment:', err);
      setFormError('Nie udało się zapisać sprzętu');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    setIsDeleting(true);
    try {
      const { error: err } = await supabase.from('equipment').delete().eq('id', id);
      if (err) throw err;
      await fetchEquipment();
      setDeleteTarget(null);
      setSuccess('Sprzęt został usunięty');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error deleting equipment:', err);
      setError('Nie udało się usunąć sprzętu');
    } finally {
      setIsDeleting(false);
    }
  };

  // ── Loading ───────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="h-8 w-56 skel" />
            <div className="h-4 w-40 skel mt-2" />
          </div>
          <div className="h-10 w-40 skel" />
        </div>
        <div className="h-10 skel" />
        <div className="card tablecard">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex items-center gap-4 p-4 border-b border-aurora-border">
              <div className="h-10 w-10 skel" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-40 skel" />
                <div className="h-3 w-28 skel" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="pagehead">
        <div>
          <h1 className="pagehead__title flex items-center gap-2">
            <Cog className="w-6 h-6 text-cyan-400" />
            Sprzęt
          </h1>
          <p className="pagehead__sub">
            Zarządzaj sprzętem organizacji (<span className="mono">{equipment.length}</span>)
          </p>
        </div>
        {canManage && (
          <div className="pagehead__actions">
            <button onClick={openAddModal} className="btn btn--primary">
              <Plus className="w-5 h-5" />
              Dodaj sprzęt
            </button>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="statcard">
          <div className="statcard__top">
            <span className="statcard__ico bg-cyan-500/15 text-cyan-400">
              <Cog />
            </span>
            <span className="statcard__lbl">Łącznie</span>
          </div>
          <div className="statcard__num">{equipment.length}</div>
        </div>
        <div className="statcard">
          <div className="statcard__top">
            <span className="statcard__ico bg-green-500/15 text-green-400">
              <CheckCircle />
            </span>
            <span className="statcard__lbl">Sprawnych</span>
          </div>
          <div className="statcard__num">
            {equipment.filter((e) => e.status === 'operational').length}
          </div>
        </div>
        <div className="statcard">
          <div className="statcard__top">
            <span className="statcard__ico bg-amber-500/15 text-amber-400">
              <AlertTriangle />
            </span>
            <span className="statcard__lbl">Ostrzeżenia/Krytyczne</span>
          </div>
          <div className="statcard__num">
            {equipment.filter((e) => e.status === 'warning' || e.status === 'critical').length}
          </div>
        </div>
        <div className="statcard">
          <div className="statcard__top">
            <span className="statcard__ico">
              <Wrench />
            </span>
            <span className="statcard__lbl">W serwisie</span>
          </div>
          <div className="statcard__num">
            {equipment.filter((e) => e.status === 'maintenance').length}
          </div>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-500/10 border border-red-500/30 rounded-ids-lg text-red-400">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError(null)}>
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 p-4 bg-green-500/10 border border-green-500/30 rounded-ids-lg text-green-400">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span>{success}</span>
        </div>
      )}

      {/* Search & Filters */}
      <div className="space-y-3">
        <div className="flex gap-3">
          <div className="inputwrap flex-1">
            <Search />
            <input
              type="text"
              placeholder="Szukaj po nazwie, kodzie, typie, producencie, modelu..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            aria-pressed={showFilters}
            className={`btn ${showFilters ? 'btn--tonal' : 'btn--secondary'}`}
          >
            <Filter className="w-4 h-4" />
            Filtry
            <ChevronDown
              className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`}
            />
          </button>
        </div>

        {showFilters && (
          <div className="card p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="field">
              <label className="field__label">Typ sprzętu</label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="select"
              >
                <option value="all">Wszystkie typy</option>
                {EQUIPMENT_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label className="field__label">Status</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="select"
              >
                <option value="all">Wszystkie statusy</option>
                {STATUS_OPTIONS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      {filteredEquipment.length === 0 ? (
        <div className="card p-12 text-center">
          <Cog className="w-12 h-12 text-aurora-subtle mx-auto mb-4" />
          <h3 className="text-lg font-medium text-aurora-text mb-2">
            {searchQuery || filterType !== 'all' || filterStatus !== 'all'
              ? 'Nie znaleziono sprzętu'
              : 'Brak sprzętu'}
          </h3>
          <p className="text-aurora-muted mb-4">
            {searchQuery
              ? 'Spróbuj zmienić kryteria wyszukiwania'
              : 'Dodaj pierwszy sprzęt do katalogu'}
          </p>
          {!searchQuery && canManage && (
            <button onClick={openAddModal} className="btn btn--primary">
              <Plus className="w-5 h-5" />
              Dodaj sprzęt
            </button>
          )}
        </div>
      ) : (
        <div className="card tablecard">
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Nazwa</th>
                  <th>Kod</th>
                  <th>Typ</th>
                  <th>Producent / Model</th>
                  <th>Status</th>
                  {canManage && <th className="text-right">Akcje</th>}
                </tr>
              </thead>
              <tbody>
                {filteredEquipment.map((item) => {
                  const statusInfo = getStatusInfo(item.status);
                  return (
                    <tr key={item.id}>
                      <td>
                        <div className="cellname">
                          <span className="w-8 h-8 rounded-ids-md bg-cyan-500/15 grid place-items-center flex-shrink-0">
                            <Cog className="w-4 h-4 text-cyan-400" />
                          </span>
                          <p className="text-sm">{item.name}</p>
                        </div>
                      </td>
                      <td>
                        {item.code ? (
                          <span className="badge mono">
                            <Hash className="w-3 h-3" />
                            {item.code}
                          </span>
                        ) : (
                          <span className="text-sm text-aurora-subtle">-</span>
                        )}
                      </td>
                      <td>
                        <span className="badge bg-cyan-500/15 text-cyan-400 border-transparent">
                          {getTypeLabel(item.type)}
                        </span>
                      </td>
                      <td className="text-sm text-aurora-muted">
                        {item.manufacturer || item.model
                          ? [item.manufacturer, item.model].filter(Boolean).join(' / ')
                          : '-'}
                      </td>
                      <td>
                        <span
                          className={`pill ${
                            item.status === 'operational'
                              ? 'pill--active'
                              : item.status === 'warning'
                                ? 'pill--trial'
                                : item.status === 'critical'
                                  ? 'pill--error'
                                  : item.status === 'maintenance'
                                    ? 'pill--pending'
                                    : 'bg-aurora-bg-subtle text-aurora-muted'
                          }`}
                        >
                          <span className="pin" />
                          {statusInfo.label}
                        </span>
                      </td>
                      {canManage && (
                        <td>
                          <div className="flex items-center justify-end gap-1 rowact">
                            <button
                              onClick={() => openEditModal(item)}
                              className="p-1.5 text-aurora-muted hover:text-aurora-text hover:bg-aurora-surface rounded-ids-md transition-colors"
                              title="Edytuj"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setDeleteTarget(item.id)}
                              className="p-1.5 text-aurora-muted hover:text-red-400 hover:bg-red-500/10 rounded-ids-md transition-colors"
                              title="Usuń"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-aurora-scrim" onClick={() => setDeleteTarget(null)} />
          <div className="relative bg-aurora-surface border border-aurora-border rounded-ids-xl shadow-elev-4 max-w-md w-full">
            <div className="modal__head">
              <h3 className="modal__title">Potwierdzenie usuwania</h3>
            </div>
            <div className="modal__body">
              <p className="text-sm text-aurora-muted">
                Czy na pewno chcesz usunąć ten sprzęt? Tej operacji nie można cofnąć.
              </p>
            </div>
            <div className="modal__foot">
              <button onClick={() => setDeleteTarget(null)} className="btn btn--ghost">
                Anuluj
              </button>
              <button
                onClick={() => handleDelete(deleteTarget)}
                disabled={isDeleting}
                className="btn btn--danger"
              >
                {isDeleting && <Loader2 className="w-4 h-4 animate-spin" />}
                Usuń
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-aurora-scrim backdrop-blur-sm" onClick={closeModal} />
          <div className="relative bg-aurora-surface rounded-ids-xl shadow-elev-4 max-w-2xl w-full border border-aurora-border max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="modal__head">
              <div className="w-10 h-10 bg-cyan-500/15 rounded-ids-md flex items-center justify-center flex-shrink-0">
                <Cog className="w-5 h-5 text-cyan-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="modal__title">
                  {editingEquipment ? 'Edytuj sprzęt' : 'Dodaj sprzęt'}
                </h2>
                <p className="text-sm text-aurora-muted truncate">
                  {editingEquipment ? editingEquipment.name : 'Wypełnij dane sprzętu'}
                </p>
              </div>
              <button onClick={closeModal} className="iconbtn">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6">
              <form id="equipment-form" onSubmit={handleSave} className="space-y-5">
                {formError && (
                  <div className="bg-red-500/10 border border-red-500/20 rounded-ids-lg p-4 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-400">{formError}</p>
                  </div>
                )}

                {/* Name */}
                <div>
                  <label className="block field__label mb-1.5">
                    <div className="flex items-center gap-1.5">
                      <Cog className="w-4 h-4" />
                      Nazwa <span className="text-aurora-brand">*</span>
                    </div>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="np. Chłodnia magazynowa, Piec konwekcyjny"
                    className="input"
                  />
                </div>

                {/* Code */}
                <div>
                  <label className="block field__label mb-1.5">
                    <div className="flex items-center gap-1.5">
                      <Hash className="w-4 h-4" />
                      Kod
                    </div>
                  </label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    placeholder="np. EQ-001, REF-01"
                    className="input mono"
                  />
                </div>

                {/* Equipment Type */}
                <div>
                  <label className="block field__label mb-1.5">
                    Typ sprzętu <span className="text-aurora-brand">*</span>
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="select"
                  >
                    {EQUIPMENT_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Status */}
                <div>
                  <label className="block field__label mb-1.5">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="select"
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Description */}
                <div>
                  <label className="block field__label mb-1.5">
                    <div className="flex items-center gap-1.5">
                      <FileText className="w-4 h-4" />
                      Opis
                    </div>
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    placeholder="Opis sprzętu..."
                    className="textarea resize-none"
                  />
                </div>

                {/* Manufacturer + Model */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block field__label mb-1.5">Producent</label>
                    <input
                      type="text"
                      value={formData.manufacturer}
                      onChange={(e) => setFormData({ ...formData, manufacturer: e.target.value })}
                      placeholder="np. Bosch, Siemens"
                      className="input"
                    />
                  </div>
                  <div>
                    <label className="block field__label mb-1.5">Model</label>
                    <input
                      type="text"
                      value={formData.model}
                      onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                      placeholder="np. KGN36, XR-500"
                      className="input"
                    />
                  </div>
                </div>

                {/* Serial number + Year of manufacture */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block field__label mb-1.5">Numer seryjny</label>
                    <input
                      type="text"
                      value={formData.serial_number}
                      onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
                      placeholder="np. SN-2024-001234"
                      className="input mono"
                    />
                  </div>
                  <div>
                    <label className="block field__label mb-1.5">Rok produkcji</label>
                    <input
                      type="number"
                      value={formData.year_of_manufacture}
                      onChange={(e) =>
                        setFormData({ ...formData, year_of_manufacture: e.target.value })
                      }
                      placeholder="np. 2023"
                      min="1900"
                      max="2100"
                      className="input"
                    />
                  </div>
                </div>

                {/* Module data (JSON) */}
                <div>
                  <label className="block field__label mb-1.5">
                    <div className="flex items-center gap-1.5">
                      <Code className="w-4 h-4" />
                      Dane modułu (JSON)
                    </div>
                  </label>
                  <textarea
                    value={formData.module_data}
                    onChange={(e) => setFormData({ ...formData, module_data: e.target.value })}
                    rows={4}
                    placeholder='{"temperature_range": "-20..5", "capacity": "500L"}'
                    className="textarea mono text-sm resize-none"
                  />
                  <p className="field__hint mt-1">
                    Dodatkowe dane specyficzne dla modułów (format JSON)
                  </p>
                </div>
              </form>
            </div>

            {/* Footer */}
            <div className="flex flex-wrap items-center justify-between gap-3 px-4 sm:px-6 py-4 border-t border-aurora-border">
              <p className="text-xs text-aurora-subtle">
                <span className="text-aurora-brand">*</span> Pola wymagane
              </p>
              <div className="flex gap-3">
                <button type="button" onClick={closeModal} className="btn btn--secondary">
                  Anuluj
                </button>
                <button
                  type="submit"
                  form="equipment-form"
                  disabled={isSaving}
                  className="btn btn--primary min-w-[140px]"
                >
                  {isSaving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  {editingEquipment ? 'Zapisz zmiany' : 'Dodaj sprzęt'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminEquipmentPage;
